import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import type { EntityTypeName } from '@/lib/crm/core/types';
import { fetchFreshSeq } from '@/lib/crm/core/utils';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { logger } from '@/lib/logger';

import {
  buildDefaultFooterConfig,
  extractFooterPropertyNames,
  type PipelineFooterConfig,
  resolveEffectiveFooterConfig,
} from '../utils/stageFooterConfig';

export interface UsePipelineFooterConfigResult {
  config: PipelineFooterConfig;
  propertyNames: string[];
  updateConfig: (next: PipelineFooterConfig) => void;
  resetConfig: () => void;
}

/**
 * Manages the pipeline stage-footer calculation configuration.
 *
 * When `pipelineEntityId` and `pipelineEntityTypeName` are provided, the config
 * is persisted on the pipeline CRM entity via the `ahoy_stage_footer_config`
 * JSON property (read from `serverFooterConfig`, written via submit_events).
 * When they are absent (e.g., create-pipeline flow where no entity exists yet),
 * the hook falls back to localStorage keyed by
 * `pipelineFooterConfig:{orgId}:{entityType}`.
 *
 * The deal-vs-non-deal default is resolved through
 * `resolveEffectiveFooterConfig`: only deal pipelines default to a footer; an
 * explicit empty `calculations: []` is a deliberate "no footer" state.
 */
export function usePipelineFooterConfig(
  orgId: number | undefined,
  entityType: string,
  isDealPipeline: boolean,
  propertySchemas: Record<string, PropertyTypeSchema>,
  fieldTypeAggregations: Record<string, string[]>,
  pipelineEntityId?: string | null,
  pipelineEntityTypeName?: string,
  serverFooterConfig?: unknown
): UsePipelineFooterConfigResult {
  const queryClient = useQueryClient();
  const { user } = useAuthInfo();

  const useEntityMode = !!(pipelineEntityId && pipelineEntityTypeName);

  const storageKey =
    !useEntityMode && orgId
      ? `pipelineFooterConfig:${orgId}:${entityType}`
      : null;

  // Version counter forces re-derivation of the config memo after a
  // localStorage write (entity mode re-derives from serverFooterConfig, which
  // the parent updates via query invalidation).
  const [localVersion, setLocalVersion] = useState(0);

  const config = useMemo(() => {
    // --- Entity-backed mode ---
    if (useEntityMode) {
      return resolveEffectiveFooterConfig(
        serverFooterConfig,
        isDealPipeline,
        propertySchemas,
        fieldTypeAggregations
      );
    }

    // --- localStorage fallback ---
    if (!storageKey) {
      return buildDefaultFooterConfig(isDealPipeline);
    }

    try {
      void localVersion; // force re-read after each persist/reset
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return buildDefaultFooterConfig(isDealPipeline);
      }
      const parsed: unknown = JSON.parse(raw);
      return resolveEffectiveFooterConfig(
        parsed,
        isDealPipeline,
        propertySchemas,
        fieldTypeAggregations
      );
    } catch {
      return buildDefaultFooterConfig(isDealPipeline);
    }
  }, [
    useEntityMode,
    serverFooterConfig,
    isDealPipeline,
    propertySchemas,
    fieldTypeAggregations,
    storageKey,
    localVersion,
  ]);

  const propertyNames = useMemo(
    () => extractFooterPropertyNames(config),
    [config]
  );

  const updateConfig = useCallback(
    (next: PipelineFooterConfig) => {
      // --- Entity-backed mode: submit property update event ---
      if (
        useEntityMode &&
        orgId &&
        pipelineEntityId &&
        pipelineEntityTypeName
      ) {
        const userId = user?.id;
        if (!userId) {
          logger.warn('Cannot save footer config: user not available');
          return;
        }

        void (async () => {
          try {
            const seq = await fetchFreshSeq(
              orgId,
              pipelineEntityTypeName,
              pipelineEntityId
            );
            await submitEventsOptimistic({
              orgId,
              userId,
              entityTypeName: pipelineEntityTypeName as EntityTypeName,
              primaryEntityId: pipelineEntityId,
              fallbackSeq: seq,
              errorToast: false,
              build: b =>
                b.propertiesUpdated(pipelineEntityId, pipelineEntityTypeName, {
                  ahoy_stage_footer_config: next,
                }),
            });

            await queryClient.invalidateQueries({
              queryKey: ['pipelines', orgId],
              exact: false,
            });
          } catch (err) {
            logger.error(
              'Failed to save pipeline footer config',
              err instanceof Error ? err : new Error(String(err)),
              {
                org_id: orgId,
                entity_id: pipelineEntityId,
              }
            );
          }
        })();

        return;
      }

      // --- localStorage fallback ---
      if (!storageKey) {
        return;
      }
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
        setLocalVersion(v => v + 1);
      } catch (err) {
        logger.warn('Failed to persist pipeline footer config', {
          storage_key: storageKey,
          error: err,
        });
      }
    },
    [
      useEntityMode,
      orgId,
      pipelineEntityId,
      pipelineEntityTypeName,
      user,
      queryClient,
      storageKey,
    ]
  );

  const resetConfig = useCallback(() => {
    if (useEntityMode && orgId && pipelineEntityId && pipelineEntityTypeName) {
      const userId = user?.id;
      if (!userId) {
        return;
      }

      // Clear the footer config by setting it to null on the entity.
      void (async () => {
        try {
          const seq = await fetchFreshSeq(
            orgId,
            pipelineEntityTypeName,
            pipelineEntityId
          );
          await submitEventsOptimistic({
            orgId,
            userId,
            entityTypeName: pipelineEntityTypeName as EntityTypeName,
            primaryEntityId: pipelineEntityId,
            fallbackSeq: seq,
            errorToast: false,
            build: b =>
              b.propertiesUpdated(pipelineEntityId, pipelineEntityTypeName, {
                ahoy_stage_footer_config: null,
              }),
          });

          await queryClient.invalidateQueries({
            queryKey: ['pipelines', orgId],
            exact: false,
          });
        } catch (err) {
          logger.error(
            'Failed to reset pipeline footer config',
            err instanceof Error ? err : new Error(String(err)),
            {
              org_id: orgId,
              entity_id: pipelineEntityId,
            }
          );
        }
      })();

      return;
    }

    // localStorage fallback
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
        setLocalVersion(v => v + 1);
      } catch (err) {
        logger.warn('Failed to reset pipeline footer config', {
          storage_key: storageKey,
          error: err,
        });
      }
    } else {
      setLocalVersion(v => v + 1);
    }
  }, [
    useEntityMode,
    orgId,
    pipelineEntityId,
    pipelineEntityTypeName,
    user,
    queryClient,
    storageKey,
  ]);

  return useMemo(
    () => ({
      config,
      propertyNames,
      updateConfig,
      resetConfig,
    }),
    [config, propertyNames, updateConfig, resetConfig]
  );
}
